// ===================================================================
// KAIXO - LeadNotes
// ===================================================================
// Timeline de notas de un lead, embebido en la ficha del CRMModal.
// Cada nota: texto + autor + fecha exacta + tipo de interacción.
// Textarea para añadir nueva nota → persiste, refresca timeline.
//
// Estética: cards sobrias en celeste, sin saturar.
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { crmGetLeadNotes, crmCreateNote, crmDeleteNote } from '../../services/crmApi';
import { Icons } from '../Icons/Icons';
import './LeadNotes.css';

const LABELS = {
    español: {
        title: 'Historial de notas', empty: 'Aún no hay notas con este contacto.',
        addPlaceholder: 'Anota lo más importante de esta interacción...',
        type: 'Tipo', save: 'Guardar nota', loading: 'Cargando...',
        deleted: 'Nota borrada', byShort: 'por'
    },
    english: {
        title: 'Notes history', empty: 'No notes for this contact yet.',
        addPlaceholder: 'Note the key info from this interaction...',
        type: 'Type', save: 'Save note', loading: 'Loading...',
        deleted: 'Note deleted', byShort: 'by'
    }
};
const TYPE_LABELS = {
    español: { call:'Llamada', message:'Mensaje', visit:'Visita', email:'Email', meeting:'Reunión', other:'Otra' },
    english: { call:'Call',     message:'Message', visit:'Visit',  email:'Email', meeting:'Meeting', other:'Other' }
};
function getLang() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u.language || 'español';
    } catch { return 'español'; }
}
function lx() { return LABELS[getLang()] || LABELS.español; }
function tlx() { return TYPE_LABELS[getLang()] || TYPE_LABELS.español; }

function fmtDateTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch { return ''; }
}

const TYPE_ICONS = {
    call: '📞', message: '💬', visit: '📍', email: '✉️', meeting: '🤝', other: '⭐'
};

export default function LeadNotes({ leadId, currentUserId, embedded = false }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const [type, setType] = useState('call');
    const [saving, setSaving] = useState(false);
    const labels = lx();
    const tLabels = tlx();

    const load = useCallback(async () => {
        if (!leadId) return;
        setLoading(true);
        try {
            const r = await crmGetLeadNotes(leadId);
            setNotes(r?.data?.notes || []);
        } catch (e) { console.warn('crmGetLeadNotes:', e?.message); }
        setLoading(false);
    }, [leadId]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        const t = text.trim();
        if (!t) return;
        setSaving(true);
        try {
            await crmCreateNote(leadId, { text: t, interactionType: type });
            setText('');
            await load();
        } catch (e) {
            alert(e?.response?.data?.error || e.message);
        }
        setSaving(false);
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('¿Borrar esta nota?')) return;
        // Optimistic delete pero guardamos copia para rollback si la API
        // rechaza (permisos, nota ya borrada, etc.). Antes el delete
        // optimista era ciego: si fallaba, la nota quedaba "fantasma"
        // hasta el siguiente reload.
        const prev = notes;
        setNotes(prev.filter(n => String(n._id) !== String(noteId)));
        try {
            await crmDeleteNote(noteId);
        } catch (e) {
            setNotes(prev); // rollback
            alert(e?.response?.data?.error || e.message);
        }
    };

    return (
        <div className={`lead-notes ${embedded ? 'lead-notes-embedded' : ''}`}>
            <h4 className="lead-notes-title">
                <Icons.Edit size={14} /> {labels.title}
                <span className="lead-notes-count">{notes.length}</span>
            </h4>

            {/* Composer */}
            <div className="lead-notes-composer">
                <textarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={labels.addPlaceholder}
                    rows={3}
                    maxLength={4000}
                />
                <div className="lead-notes-composer-foot">
                    <select value={type} onChange={e => setType(e.target.value)}>
                        <option value="call">📞 {tLabels.call}</option>
                        <option value="message">💬 {tLabels.message}</option>
                        <option value="visit">📍 {tLabels.visit}</option>
                        <option value="email">✉️ {tLabels.email}</option>
                        <option value="meeting">🤝 {tLabels.meeting}</option>
                        <option value="other">⭐ {tLabels.other}</option>
                    </select>
                    <button type="button"
                            className="lead-notes-save-btn"
                            disabled={!text.trim() || saving}
                            onClick={handleSave}>
                        {saving ? '…' : labels.save}
                    </button>
                </div>
            </div>

            {/* Timeline */}
            <div className="lead-notes-timeline">
                {loading && notes.length === 0 && (
                    <div className="lead-notes-empty">{labels.loading}</div>
                )}
                {!loading && notes.length === 0 && (
                    <div className="lead-notes-empty">{labels.empty}</div>
                )}
                {notes.map(n => {
                    const isAuthor = currentUserId && String(n.agent?._id || n.agent) === String(currentUserId);
                    const icon = TYPE_ICONS[n.interactionType] || TYPE_ICONS.other;
                    const typeLbl = tLabels[n.interactionType] || tLabels.other;
                    const author = n.agent?.name || '—';
                    return (
                        <div key={n._id} className="lead-note-item">
                            <div className="lead-note-head">
                                <span className="lead-note-type">{icon} {typeLbl}</span>
                                <span className="lead-note-when">{fmtDateTime(n.createdAt)}</span>
                                {isAuthor && (
                                    <button type="button" className="lead-note-del" onClick={() => handleDelete(n._id)} title="Borrar">
                                        <Icons.Trash size={11} color="#999" />
                                    </button>
                                )}
                            </div>
                            <div className="lead-note-text">{n.text}</div>
                            <div className="lead-note-author">{labels.byShort} {author}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
